/**
 * ProfileRepository — profile, settings, data export, account deletion.
 *
 * - `deleteMyAccount` calls the `delete-my-account` Edge Function. When the
 *   function is absent (not deployed) it throws a typed, honest error —
 *   NEVER a fake success.
 * - `exportMyData` reads every user-owned table and returns a JSON Blob.
 *   Storage objects are exported as object paths (never signed URLs in an
 *   export artifact that may be shared).
 */
import { requireSupabase } from '@/lib/supabase/client'
import { makeAppError, newCorrelationId } from '@/lib/supabase/errors'
import {
  appNotificationFromRow,
  auditEventFromRow,
  breathingSessionFromRow,
  caregiverClientLinkFromRow,
  dailyHealthFromRow,
  healthItemFromRow,
  healthLogFromRow,
  journalEntryFromRow,
  medicationIntakeEventFromRow,
  medicationOccurrenceFromRow,
  medicationScheduleFromRow,
  noteFromRow,
  placeFromRow,
  plannerItemFromRow,
  profileFromRow,
  profileToPatch,
  userSettingsFromRow,
  userSettingsToRow,
  type AppNotificationRow,
  type AuditEventRow,
  type BreathingSessionRow,
  type CaregiverClientLinkRow,
  type DailyHealthRow,
  type HealthItemRow,
  type HealthLogRow,
  type JournalEntryRow,
  type MedicationIntakeEventRow,
  type MedicationOccurrenceRow,
  type MedicationScheduleRow,
  type NoteRow,
  type PlaceRow,
  type PlannerItemRow,
  type ProfileRow,
  type UserSettingsRow,
} from '@/lib/supabase/mappers'
import type { Profile, UserSettings } from '@/types'
import type { ProfileRepository } from '../interfaces'
import { assertRowReturned, fail, requireUserId } from './helpers'

async function listRows<Row, Domain>(
  table: string,
  column: string,
  value: string,
  map: (row: Row) => Domain,
): Promise<Domain[]> {
  const supabase = requireSupabase()
  const { data, error } = await supabase.from(table).select('*').eq(column, value)
  if (error) fail(error)
  return ((data ?? []) as Row[]).map(map)
}

function accountDeletionUnavailable(technicalMessage: string) {
  return makeAppError({
    code: 'ACCOUNT_DELETION_UNAVAILABLE',
    userMessage:
      'Account verwijderen is nu niet beschikbaar. Neem contact op met de begeleiding of probeer het later opnieuw.',
    technicalMessage,
    retryable: false,
    source: 'network',
    correlationId: newCorrelationId(),
  })
}

export const profileRepository: ProfileRepository = {
  async getMyProfile(): Promise<Profile | null> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()
    if (error) fail(error)
    return data ? profileFromRow(data as ProfileRow) : null
  },

  async upsertMyProfile(input): Promise<Profile> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const patch = profileToPatch(input)
    // Role is never writable here; a DB guard trigger blocks browser role
    // changes as a second layer of defence.
    const { data, error } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', userId)
      .select()
      .single()
    return profileFromRow(assertRowReturned(data, error) as ProfileRow)
  },

  async getSettings(): Promise<UserSettings | null> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()
    if (error) fail(error)
    return data ? userSettingsFromRow(data as UserSettingsRow) : null
  },

  async upsertSettings(settings): Promise<UserSettings> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const row = { user_id: userId, ...userSettingsToRow(settings) }
    const { data, error } = await supabase
      .from('user_settings')
      .upsert(row, { onConflict: 'user_id' })
      .select()
      .single()
    return userSettingsFromRow(assertRowReturned(data, error) as UserSettingsRow)
  },

  async exportMyData(): Promise<Blob> {
    const userId = await requireUserId()

    const profile = await profileRepository.getMyProfile()
    const settings = await profileRepository.getSettings()

    const [
      plannerItems,
      notes,
      journalEntries,
      healthItems,
      healthLogs,
      dailyHealth,
      places,
      breathingSessions,
      links,
      schedules,
      occurrences,
      intakeEvents,
      notifications,
      auditEvents,
    ] = await Promise.all([
      listRows<PlannerItemRow, unknown>('planner_items', 'user_id', userId, plannerItemFromRow),
      listRows<NoteRow, unknown>('notes', 'user_id', userId, noteFromRow),
      listRows<JournalEntryRow, unknown>('journal_entries', 'user_id', userId, journalEntryFromRow),
      listRows<HealthItemRow, unknown>('health_items', 'user_id', userId, healthItemFromRow),
      listRows<HealthLogRow, unknown>('health_logs', 'user_id', userId, healthLogFromRow),
      listRows<DailyHealthRow, unknown>('daily_health', 'user_id', userId, dailyHealthFromRow),
      listRows<PlaceRow, unknown>('places', 'user_id', userId, placeFromRow),
      listRows<BreathingSessionRow, unknown>('breathing_sessions', 'user_id', userId, breathingSessionFromRow),
      listRows<CaregiverClientLinkRow, unknown>('caregiver_client_links', 'client_id', userId, caregiverClientLinkFromRow),
      listRows<MedicationScheduleRow, unknown>('medication_schedules', 'client_id', userId, medicationScheduleFromRow),
      listRows<MedicationOccurrenceRow, unknown>('medication_occurrences', 'client_id', userId, medicationOccurrenceFromRow),
      listRows<MedicationIntakeEventRow, unknown>('medication_intake_events', 'client_id', userId, medicationIntakeEventFromRow),
      listRows<AppNotificationRow, unknown>('notifications', 'recipient_id', userId, appNotificationFromRow),
      listRows<AuditEventRow, unknown>('audit_events', 'subject_user_id', userId, auditEventFromRow),
    ])

    const payload = {
      exportedAt: new Date().toISOString(),
      userId,
      profile,
      settings,
      plannerItems,
      notes,
      journalEntries,
      healthItems,
      healthLogs,
      dailyHealth,
      places,
      breathingSessions,
      caregiverClientLinks: links,
      medicationSchedules: schedules,
      medicationOccurrences: occurrences,
      medicationIntakeEvents: intakeEvents,
      notifications,
      auditEvents,
    }
    return new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  },

  async deleteMyAccount(): Promise<void> {
    const supabase = requireSupabase()
    await requireUserId()
    try {
      const { error } = await supabase.functions.invoke('delete-my-account', { body: {} })
      if (error) {
        const message = (error.message ?? '').toLowerCase()
        const context = (error as { context?: { status?: number } }).context
        if (
          context?.status === 404 ||
          message.includes('not found') ||
          (message.includes('function') && message.includes('exist')) ||
          (message.includes('failed to fetch') && message.includes('function'))
        ) {
          throw accountDeletionUnavailable(error.message ?? 'edge function not found')
        }
        fail(error, 'network')
      }
      // The function confirms deletion server-side; local session cleanup is
      // the caller's (auth layer) responsibility.
    } catch (error) {
      if (typeof error === 'object' && error !== null && 'code' in error) throw error
      fail(error, 'network')
    }
  },
}
